import axios from "axios";

// http://localhost:8000/api/

const apiBaseUrl = process.env.VUE_APP_API_BASE || '/api';

const apiClient = (baseURL = apiBaseUrl, timeout = 60000) => {
    const options = {
        baseURL: baseURL,
        timeout: timeout,
        withCredentials: true,
        headers: {
            "Content-Type": "application/json"
        }
    }

    const client = axios.create(options);

    client.interceptors.request.use(
        (config) => {
            const accessToken = localStorage.access;
            if (accessToken) {
                config.headers["Authorization"] = `Bearer ${accessToken}`
            }
            return config;
        },
        (requestError) => {
            return Promise.reject(requestError)
        }
    )

    client.interceptors.response.use(
        (response) => response,
        (error) => {
            const config = error.config;
            if (error.response.status === 401) { // If unauthorized request, attempt to refresh our user's token.
                console.log('unauth request!')

                if (localStorage.getItem("refresh") === null) {
                    return Promise.reject(error);
                }

                if (error.config.url === '/token/refresh') {
                    // Unauthorized error encountered while refreshing token, must mean our refresh token has also expired.
                    // Force user re-authentication.
                    localStorage.removeItem('access');
                    localStorage.removeItem('refresh');

                    // Resolve promise when errors encountered on this url because it prevents continuous error loops.
                    return Promise.resolve(error);
                }

                return client.post("/token/refresh", { "refresh": localStorage.refresh }).then((response) => {
                    if (response.data && response.data.access) {
                        localStorage.access = response.data.access;
                    }
                    return client(config);
                }).catch((tokenRefreshError) => {
                    console.log('did this happen?')
                    localStorage.removeItem('access');
                    localStorage.removeItem('refresh');
                    return Promise.reject(tokenRefreshError);
                });
            }
            return Promise.reject(error);
        }
    );

    return client;
}

class APIClient {
    constructor(baseURL = apiBaseUrl, timeout=30000) {
        this.client = apiClient(baseURL, timeout)
    }

    /** Authenticate provided username and password against server to obtain JWT access and refresh tokens. */
    authenticate(username, password) {
        return this.client.post('/token', {
            "username": username,
            "password": password
        }).then(response => Promise.resolve(response)).catch(error => {
            return Promise.reject(error)
        })
    }

    /** Obtain a new access token using our user's refresh token. */
    refreshToken(refreshToken) {
        return this.client.post('/token/refresh', {
            "refreshToken": refreshToken
        }).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getAuthenticatedUser() {
        return this.client.get('/user').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postUser(payload) {
        return this.client.post('/users', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postRequestPasswordReset(payload) {
        return this.client.post('/request-password-reset', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postResetPassword(payload) {
        return this.client.post('/reset-password', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getCreditProducts() {
        return this.client.get('/store/credits/products').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStoreCreditAmount() {
        return this.client.get(`/store/credits/amount`).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getStorePurchases(type) {
        return this.client.get(`/store/purchases`,).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postPasswordChange(payload) {
        return this.client.post('/change-password', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    getTwoFactorStatus() {
        return this.client.get('/2fa/status').then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postTwoFactorDeactivation(payload) {
        return this.client.post('/2fa/deactivate', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

    postTwoFactorActivation(payload) {
        return this.client.post('/2fa/activate', payload).then(response => Promise.resolve(response)).catch(error => Promise.reject(error))
    }

}

export default APIClient
